/**
 * Standards Management Page
 * Two-layer grid for enabling/disabling standards categories and individual standards
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Card, Select, Button, Badge, Spinner, ToggleSwitch, Accordion } from 'flowbite-react';
import { HiShieldCheck, HiExclamation, HiCheck, HiX, HiRefresh } from 'react-icons/hi';
import { useStatus } from '../contexts/StatusContext';
import { projectsApi } from '../api/projects';
import { standardsApi } from '../api/standards';
import type { Project, ProjectStandards, StandardsPreferences, CategoryInfo } from '../types';

const Standards: React.FC = () => {
  const { user } = useStatus();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [projectStandards, setProjectStandards] = useState<ProjectStandards | null>(null);
  const [preferences, setPreferences] = useState<StandardsPreferences>({
    enabled_categories: {},
    standard_overrides: {},
    critical_overrides: {},
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Load project standards when project changes
  useEffect(() => {
    if (selectedProjectId) {
      loadProjectStandards(selectedProjectId);
    }
  }, [selectedProjectId]);

  const loadProjects = async () => {
    try {
      const response = await projectsApi.list();
      if (response.success && response.data) {
        const activeProjects = (response.data as any).Records?.filter(
          (p: Project) => p.status !== 'archived'
        ) || response.data.filter((p: Project) => p.status !== 'archived');
        setProjects(activeProjects);
        if (activeProjects.length > 0 && !selectedProjectId) {
          setSelectedProjectId(activeProjects[0].project_id);
        }
      }
    } catch (err) {
      setError('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  const loadProjectStandards = async (projectId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await standardsApi.getProjectStandards(projectId);
      if (response.success && response.data) {
        const data = (response.data as any).data || response.data;
        setProjectStandards(data);
        setPreferences({
          enabled_categories: data.preferences?.enabled_categories || {},
          standard_overrides: data.preferences?.standard_overrides || {},
          critical_overrides: data.preferences?.critical_overrides || {},
        });
        setHasChanges(false);
      } else {
        setError(response.error || 'Failed to load standards');
      }
    } catch (err) {
      setError('Failed to load project standards');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryToggle = useCallback((categoryKey: string, enabled: boolean) => {
    setPreferences(prev => ({
      ...prev,
      enabled_categories: {
        ...prev.enabled_categories,
        [categoryKey]: enabled,
      },
    }));
    setHasChanges(true);
    setSuccessMessage(null);
  }, []);

  const handleStandardOverride = useCallback((standardPath: string, enabled: boolean | null) => {
    setPreferences(prev => {
      const newOverrides = { ...prev.standard_overrides };
      if (enabled === null) {
        // Remove override (use category default)
        delete newOverrides[standardPath];
      } else {
        newOverrides[standardPath] = enabled;
      }
      return {
        ...prev,
        standard_overrides: newOverrides,
      };
    });
    setHasChanges(true);
    setSuccessMessage(null);
  }, []);

  const handleSave = async () => {
    if (!selectedProjectId) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await standardsApi.updateProjectStandards(selectedProjectId, preferences);
      if (response.success) {
        setSuccessMessage('Standards preferences saved successfully');
        setHasChanges(false);
        // Reload to get updated data
        await loadProjectStandards(selectedProjectId);
      } else {
        setError(response.error || 'Failed to save preferences');
      }
    } catch (err) {
      setError('Failed to save standards preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (projectStandards) {
      setPreferences({
        enabled_categories: projectStandards.preferences?.enabled_categories || {},
        standard_overrides: projectStandards.preferences?.standard_overrides || {},
        critical_overrides: projectStandards.preferences?.critical_overrides || {},
      });
      setHasChanges(false);
      setSuccessMessage(null);
    }
  };

  const isCategoryEnabled = (categoryKey: string): boolean => {
    // If explicitly set, use that value
    if (categoryKey in preferences.enabled_categories) {
      return preferences.enabled_categories[categoryKey];
    }
    // Default: enabled
    return true;
  };

  const getCategoryStats = (categoryKey: string): { enabled: number; total: number } => {
    const category = projectStandards?.catalog?.categories?.[categoryKey];
    const total = category?.standards_count || 0;
    const categoryEnabled = isCategoryEnabled(categoryKey);

    // Count overrides for this category
    let overrideCount = 0;
    Object.entries(preferences.standard_overrides).forEach(([path, enabled]) => {
      if (path.startsWith(categoryKey + '/')) {
        if (enabled !== categoryEnabled) {
          overrideCount++;
        }
      }
    });

    const enabled = categoryEnabled ? total - overrideCount : overrideCount;
    return { enabled, total };
  };

  if (isLoading && !projectStandards) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Spinner size="xl" />
      </div>
    );
  }

  if (projects.length === 0 && !isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Standards Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure which standards apply to your projects
          </p>
        </div>
        <Card>
          <div className="flex flex-col items-center justify-center py-12">
            <HiShieldCheck className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No projects yet</h3>
            <p className="text-gray-500 text-center max-w-md mb-4">
              Create a project first, then configure which standards apply to it.
            </p>
            <a href="/projects" className="text-mindmeld-primary hover:text-blue-700 font-medium">
              Go to Projects
            </a>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Standards Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure which standards apply to your projects
          </p>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <Button color="gray" onClick={handleReset}>
              <HiRefresh className="mr-2 h-4 w-4" />
              Reset
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            color={hasChanges ? 'success' : 'gray'}
          >
            {isSaving ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              <>
                <HiCheck className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Project Selector */}
      <Card>
        <div className="flex items-center gap-4">
          <label htmlFor="project" className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Select Project:
          </label>
          <Select
            id="project"
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            className="flex-1 max-w-md"
          >
            {projects.map((project) => (
              <option key={project.project_id} value={project.project_id}>
                {project.project_name}
              </option>
            ))}
          </Select>
          {projectStandards && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Badge color={projectStandards.is_enterprise ? 'purple' : 'blue'}>
                {projectStandards.catalog_id}
              </Badge>
              <span>{projectStandards.catalog?.total_standards || 0} standards available</span>
            </div>
          )}
        </div>
      </Card>

      {/* Messages */}
      {error && (
        <div className="p-4 text-red-700 bg-red-100 rounded-md flex items-center gap-2">
          <HiExclamation className="h-5 w-5" />
          {error}
        </div>
      )}
      {successMessage && (
        <div className="p-4 text-green-700 bg-green-100 rounded-md flex items-center gap-2">
          <HiCheck className="h-5 w-5" />
          {successMessage}
        </div>
      )}

      {/* Standards Categories */}
      {projectStandards && projectStandards.catalog?.categories && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <HiShieldCheck className="h-5 w-5 text-mindmeld-primary" />
            Standards Categories
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            Toggle entire categories on or off. You can also expand each category to override individual standards.
          </p>

          <Accordion collapseAll>
            {Object.entries(projectStandards.catalog.categories).map(([categoryKey, categoryInfo]) => {
              const info = categoryInfo as CategoryInfo;
              const enabled = isCategoryEnabled(categoryKey);
              const stats = getCategoryStats(categoryKey);

              return (
                <Accordion.Panel key={categoryKey}>
                  <Accordion.Title>
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-3">
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center"
                        >
                          <ToggleSwitch
                            checked={enabled}
                            onChange={(checked) => handleCategoryToggle(categoryKey, checked)}
                          />
                        </div>
                        <div>
                          <span className="font-medium">{info.name || categoryKey}</span>
                          <p className="text-sm text-gray-500 font-normal">
                            {info.description}
                          </p>
                        </div>
                      </div>
                      <Badge color={enabled ? 'success' : 'gray'}>
                        {stats.enabled}/{stats.total} enabled
                      </Badge>
                    </div>
                  </Accordion.Title>
                  <Accordion.Content>
                    <div className="space-y-3">
                      <p className="text-xs text-gray-500 mb-4">
                        Override individual standards within this category. Standards follow the category setting unless overridden.
                      </p>
                      {projectStandards.catalog?.layers?.map((layer) => {
                        const layerStandards = layer.files.filter((f) =>
                          f.startsWith(categoryKey + '/') ||
                          (categoryKey === 'foundation' && !f.includes('/'))
                        );
                        if (layerStandards.length === 0) return null;

                        return (
                          <div key={layer.name} className="border-l-2 border-gray-200 pl-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">
                              {layer.name}
                            </h4>
                            <div className="space-y-2">
                              {layerStandards.map((file) => {
                                const standardPath = file;
                                const hasOverride = standardPath in preferences.standard_overrides;
                                const isEnabled = hasOverride
                                  ? preferences.standard_overrides[standardPath]
                                  : enabled;
                                const fileName = file.split('/').pop()?.replace('.md', '') || file;

                                return (
                                  <div
                                    key={file}
                                    className="flex items-center justify-between py-1 px-2 rounded hover:bg-gray-50"
                                  >
                                    <div className="flex items-center gap-2">
                                      {isEnabled ? (
                                        <HiCheck className="h-4 w-4 text-green-500" />
                                      ) : (
                                        <HiX className="h-4 w-4 text-gray-400" />
                                      )}
                                      <span className={`text-sm ${isEnabled ? 'text-gray-900' : 'text-gray-400'}`}>
                                        {fileName}
                                      </span>
                                      {hasOverride && (
                                        <Badge color="yellow" size="xs">
                                          override
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {hasOverride && (
                                        <button
                                          onClick={() => handleStandardOverride(standardPath, null)}
                                          className="text-xs text-gray-500 hover:text-gray-700"
                                        >
                                          Reset
                                        </button>
                                      )}
                                      <ToggleSwitch
                                        checked={isEnabled}
                                        onChange={(checked) => handleStandardOverride(standardPath, checked)}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Accordion.Content>
                </Accordion.Panel>
              );
            })}
          </Accordion>
        </Card>
      )}

      {/* Summary */}
      {projectStandards && (
        <Card>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">
                {Object.values(preferences.enabled_categories).filter(v => v === true).length ||
                  Object.keys(projectStandards.catalog?.categories || {}).length}
              </p>
              <p className="text-xs text-gray-500">Categories Enabled</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">
                {Object.keys(preferences.standard_overrides).length}
              </p>
              <p className="text-xs text-gray-500">Individual Overrides</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">
                {projectStandards.catalog?.total_standards || 0}
              </p>
              <p className="text-xs text-gray-500">Total Available</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-mindmeld-primary">
                {projectStandards.catalog?.layers?.length || 0}
              </p>
              <p className="text-xs text-gray-500">Layers</p>
            </div>
          </div>
          {projectStandards.modified_at && (
            <p className="text-xs text-gray-400 mt-4">
              Last modified: {new Date(projectStandards.modified_at).toLocaleString()}
              {projectStandards.modified_by && ` by ${projectStandards.modified_by}`}
            </p>
          )}
        </Card>
      )}
    </div>
  );
};

export default Standards;
